import { finish } from './work.ts';
// Core: pure functions, no DOM. Bundled into the page and imported directly
// by the tests, benches and scripts.

import type {
  DisplayPosition,
  GeometryFile,
  LatLngTuple,
  LatLon,
  LineId,
  LineLayout,
  Network,
  NetworkLine,
  PathSegment,
  ProjectedTrack,
  Rail,
  RouteResponse,
  RouteStop,
  ScreenPoint,
  ScreenTrackPoint,
  SegmentGeometry,
  SnapReason,
  Station,
  StationId,
  StationSequence,
  Stop,
  TrackPoint,
  TrackView,
  TrackedTrain,
  TrackedTrains,
  TrainId,
  TrainMotion,
  TrainPrediction,
  TrainTarget,
} from './types.ts';

// Learned or estimated run time between two adjacent stations, in seconds.
export type RunTime = (lineId: LineId, a: StationId, b: StationId) => number;

// A point on a rail: where a train is drawn, before its segment key.
interface RailPoint extends LatLon {
  readonly ang: number | null;
  readonly P: StationId | null;
  readonly N: StationId;
  readonly f: number;
}

// Built in place by buildNetwork, returned as a readonly NetworkLine.
interface LineDraft {
  readonly id: LineId;
  readonly ids: Set<StationId>;
  readonly adj: Map<StationId, Set<StationId>>;
  readonly outPrev: Map<StationId, Set<StationId>>;
  readonly outNext: Map<StationId, Set<StationId>>;
  readonly alias: Map<string, StationId>;
  readonly seqs: StationSequence[];
}

interface StationDraft extends Station {
  readonly lines: Set<LineId>;
}

// computeTrains fills in destKey after the rest.

const EARTH = 6371000;
const RAD = Math.PI / 180;

// Equirectangular distance in meters. Plenty accurate at city scale.
function dist(a: LatLon, b: LatLon): number {
  const x = (b.lon - a.lon) * RAD * Math.cos(((a.lat + b.lat) / 2) * RAD);
  const y = (b.lat - a.lat) * RAD;
  return Math.sqrt(x * x + y * y) * EARTH;
}

// A runtime check only: the types already say it is an array, and
// Array.isArray would narrow a readonly array to any[].
function isList(v: unknown): boolean {
  return Array.isArray(v);
}

// `a || b` for optional strings.
function either(a: string | undefined, b: string): string {
  return a === undefined || a === '' ? b : a;
}

function cleanName(s: string | undefined): string {
  return (s ?? '')
    .replace(/\s+(Underground|DLR|Rail|Overground|Elizabeth line)\s+Station$/iu, '')
    .replace(/\s+Station$/iu, '')
    .replace(/\s+(Underground|DLR)$/iu, '')
    .trim();
}

function normName(s: string | undefined): string {
  return cleanName(s)
    .toLowerCase()
    .replaceAll('&', 'and')
    .replaceAll(/[^a-z0-9]+/gu, ' ')
    .trim();
}

// Same, minus bracketed qualifiers: "Abbey Wood (London)" -> "abbey wood".
function baseName(s: string | undefined): string {
  return normName((s ?? '').replaceAll(/\s*\([^)]*\)/gu, ' '));
}

function stationOf(net: Network, id: StationId): Station {
  const s = net.stations.get(id);
  if (!s) throw new Error('unknown station ' + id);
  return s;
}

// responses: [{ lineId, direction, data }] where data is a TfL
// /Line/{id}/Route/Sequence/{direction} payload.
function buildNetwork(responses: readonly RouteResponse[]): Network {
  return finish(buildNetworkSteps(responses));
}

function* buildNetworkSteps(responses: readonly RouteResponse[]): Generator<void, Network, void> {
  const stations = new Map<StationId, StationDraft>();
  const lines: Partial<Record<LineId, LineDraft>> = {};
  const stopAliases = new WeakMap<RouteStop, readonly (string | undefined)[]>();
  for (const { lineId, direction, data } of responses) {
    if (data === null || data === undefined || !isList(data.stopPointSequences)) continue;
    let L = lines[lineId];
    if (!L) {
      L = {
        id: lineId,
        ids: new Set(),
        adj: new Map(),
        outPrev: new Map(),
        outNext: new Map(),
        alias: new Map(),
        seqs: [],
      };
      lines[lineId] = L;
    }
    const graph = L;
    const link = (kind: 'adj' | 'outNext' | 'outPrev', from: StationId, to: StationId): void => {
      const edges = graph[kind];
      let neighbors = edges.get(from);
      if (!neighbors) edges.set(from, (neighbors = new Set()));
      neighbors.add(to);
    };
    for (const sps of data.stopPointSequences) {
      yield;
      const dir = either(sps.direction, direction).toLowerCase();
      const ids: StationId[] = [];
      for (const sp of sps.stopPoint ?? []) {
        if (
          sp === null ||
          sp === undefined ||
          sp.id === undefined ||
          sp.id === '' ||
          typeof sp.lat !== 'number' ||
          typeof sp.lon !== 'number'
        )
          continue;
        const id = sp.id;
        let station = stations.get(id);
        if (!station) {
          station = { id, name: cleanName(sp.name), lat: sp.lat, lon: sp.lon, lines: new Set() };
          stations.set(id, station);
        }
        station.lines.add(lineId);
        L.ids.add(id);
        let aliases = stopAliases.get(sp);
        if (!aliases) {
          aliases = [
            sp.stationId,
            sp.topMostParentId,
            sp.icsId === undefined || sp.icsId === '' ? undefined : 'ics:' + sp.icsId,
            'n:' + normName(sp.name),
            'b:' + baseName(sp.name),
          ];
          stopAliases.set(sp, aliases);
        }
        for (const a of aliases) if (a !== undefined && a !== '' && !L.alias.has(a)) L.alias.set(a, id);
        if (ids.at(-1) !== id) ids.push(id);
      }
      if (ids.length > 0) L.seqs.push({ dir, ids });
      let a: StationId | undefined;
      for (const b of ids) {
        if (a !== undefined) {
          // outNext/outPrev always describe the "outbound" direction.
          link('adj', a, b);
          link('adj', b, a);
          if (dir === 'inbound') {
            link('outNext', b, a);
            link('outPrev', a, b);
          } else {
            link('outNext', a, b);
            link('outPrev', b, a);
          }
        }
        a = b;
      }
    }
  }
  return { stations, lines };
}

// ---- real track geometry (public/geometry.json, built from OpenStreetMap)

function decodePolyline(str: string): LatLngTuple[] {
  const pts: LatLngTuple[] = [];
  let i = 0;
  let lat = 0;
  let lon = 0;
  const next = (): number => {
    let r = 0;
    let sh = 0;
    let b: number;
    do {
      b = (str.codePointAt(i++) ?? Number.NaN) - 63;
      r |= (b & 0x1f) << sh;
      sh += 5;
    } while (b >= 0x20);
    return r & 1 ? ~(r >> 1) : r >> 1;
  };
  while (i < str.length) {
    lat += next();
    lon += next();
    pts.push([lat / 1e5, lon / 1e5]);
  }
  return pts;
}

// public/geometry.json, as far as attachGeometry needs to trust it.
function isGeometryFile(v: unknown): v is GeometryFile {
  if (typeof v !== 'object' || v === null) return false;
  if (!('segments' in v) || v.segments === undefined) return true;
  return typeof v.segments === 'object' && v.segments !== null;
}

// Attach track shapes to the network. Ends are pinned to the exact station
// coordinates so shapes and station dots always meet.
// Returns a new network with geom set. Does not change `net`.
function attachGeometry(net: Network, geom: GeometryFile | null | undefined): Network {
  return finish(attachGeometrySteps(net, geom));
}

function* attachGeometrySteps(net: Network, geom: GeometryFile | null | undefined): Generator<void, Network, void> {
  const shapes = new Map<string, SegmentGeometry>();
  // Regional services often share the same station-to-station track. Keep
  // one decoded path and distance table for identical shapes in this network.
  const shared = new Map<string, { readonly encoded: string; readonly shape: SegmentGeometry }>();
  const segs = geom?.segments ?? {};
  for (const [lineId, lineSegs] of Object.entries(segs)) {
    const L = net.lines[lineId];
    if (!L || !lineSegs) continue;
    for (const [key, encoded] of Object.entries(lineSegs)) {
      yield;
      const [a, b] = key.split('|');
      if (a === undefined || b === undefined || !L.ids.has(a) || !L.ids.has(b) || encoded === undefined) continue;
      const cached = shared.get(key);
      if (cached?.encoded === encoded) {
        shapes.set(lineId + '|' + key, cached.shape);
        continue;
      }
      const pts = decodePolyline(encoded);
      if (pts.length < 2) continue;
      const A = stationOf(net, a);
      const B = stationOf(net, b);
      pts[0] = [A.lat, A.lon];
      pts[pts.length - 1] = [B.lat, B.lon];
      const cum: number[] = [];
      let len = 0;
      let prev: LatLngTuple | undefined;
      for (const p of pts) {
        if (prev) len += dist({ lat: prev[0], lon: prev[1] }, { lat: p[0], lon: p[1] });
        cum.push(len);
        prev = p;
      }
      const shape = { pts, cum, len };
      shared.set(key, { encoded, shape });
      shapes.set(lineId + '|' + a + '|' + b, shape);
    }
  }
  return { ...net, geom: shapes };
}

// Track shape from a to b as [[lat, lon], ...], straight if unknown.
function segPoints(net: Network, lineId: LineId, a: StationId, b: StationId): readonly LatLngTuple[] {
  const g = net.geom?.get(lineId + '|' + a + '|' + b);
  if (g) return g.pts;
  const r = net.geom?.get(lineId + '|' + b + '|' + a);
  if (r) return r.pts.toReversed();
  const A = stationOf(net, a);
  const B = stationOf(net, b);
  return [
    [A.lat, A.lon],
    [B.lat, B.lon],
  ];
}

function segLength(net: Network, lineId: LineId, a: StationId, b: StationId): number {
  const g = net.geom && (net.geom.get(lineId + '|' + a + '|' + b) ?? net.geom.get(lineId + '|' + b + '|' + a));
  return g ? g.len : dist(stationOf(net, a), stationOf(net, b));
}

// Screen-space heading (x east, y south) of a lat/lon step.
function headingOf(p: LatLngTuple, q: LatLngTuple): number {
  return Math.atan2(-(q[0] - p[0]), (q[1] - p[1]) * Math.cos(((p[0] + q[0]) / 2) * RAD));
}

const railGeometry = new WeakMap<
  Rail,
  {
    readonly net: Network;
    readonly lineId: LineId;
    readonly a: StationId;
    readonly b: StationId;
    readonly g: SegmentGeometry | undefined;
    readonly rev: boolean;
  }
>();

// Point a fraction f of the way along the track from a to b, plus the
// track's heading there.
function along(net: Network, lineId: LineId, a: StationId, b: StationId, f: number, rail?: Rail): TrackPoint {
  // Motion stays on the same segment for many frames. Resolve its shape
  // once, without rebuilding and hashing the long station-pair keys each
  // frame. A new network (including newly attached geometry) invalidates it.
  const cached = rail ? railGeometry.get(rail) : undefined;
  const same = cached?.net === net && cached.lineId === lineId && cached.a === a && cached.b === b;
  let g = same ? cached.g : net.geom?.get(lineId + '|' + a + '|' + b);
  let rev = same ? cached.rev : false;
  if (!same) {
    if (!g && net.geom) {
      g = net.geom.get(lineId + '|' + b + '|' + a);
      rev = g !== undefined;
    }
    if (rail) railGeometry.set(rail, { net, lineId, a, b, g, rev });
  }
  if (!g) {
    const A = stationOf(net, a);
    const B = stationOf(net, b);
    return {
      lat: A.lat + (B.lat - A.lat) * f,
      lon: A.lon + (B.lon - A.lon) * f,
      ang: headingOf([A.lat, A.lon], [B.lat, B.lon]),
    };
  }
  const s = (rev ? 1 - f : f) * g.len;
  let lo = 0;
  let hi = g.cum.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((g.cum[mid] ?? 0) <= s) lo = mid;
    else hi = mid;
  }
  const p = g.pts[lo];
  const q = g.pts[hi];
  const clo = g.cum[lo];
  const chi = g.cum[hi];
  if (!p || !q || clo === undefined || chi === undefined) throw new Error('empty track shape ' + lineId);
  const span = chi - clo || 1;
  const u = Math.max(0, Math.min(1, (s - clo) / span));
  return {
    lat: p[0] + (q[0] - p[0]) * u,
    lon: p[1] + (q[1] - p[1]) * u,
    ang: rev ? headingOf(q, p) : headingOf(p, q),
  };
}

// Where point p falls along the track from a to b: the fraction f, and how
// far p is from the track in meters.
function projectOnto(
  net: Network,
  lineId: LineId,
  a: StationId,
  b: StationId,
  p: LatLon,
): { readonly f: number; readonly d: number } {
  const pts = segPoints(net, lineId, a, b);
  const k = Math.cos(p.lat * RAD);
  const M = 111_320;
  let total = 0;
  let best = { s: 0, d: Infinity };
  for (let i = 1; i < pts.length; i++) {
    const A = pts[i - 1];
    const B = pts[i];
    if (!A || !B) continue;
    const bx = (B[1] - A[1]) * k * M;
    const by = (B[0] - A[0]) * M;
    const px = (p.lon - A[1]) * k * M;
    const py = (p.lat - A[0]) * M;
    const len = Math.hypot(bx, by);
    const u = len > 0 ? Math.max(0, Math.min(1, (px * bx + py * by) / (len * len))) : 0;
    const d = Math.hypot(px - bx * u, py - by * u);
    if (d < best.d) best = { s: total + u * len, d };
    total += len;
  }
  return { f: total > 0 ? best.s / total : 0, d: best.d };
}

// Polylines for drawing a line, with every track segment drawn once.
function linePaths(net: Network, lineId: LineId, withSegments: true): LineLayout;
function linePaths(net: Network, lineId: LineId, withSegments?: false): LineLayout['paths'];
function linePaths(net: Network, lineId: LineId, withSegments = false): LineLayout | LineLayout['paths'] {
  const L = net.lines[lineId];
  if (!L) return withSegments ? { paths: [], segments: new Map() } : [];
  const drawn = new Set<string>();
  const paths: LatLngTuple[][] = [];
  const segments = new Map<string, PathSegment>();
  const seqs = L.seqs.toSorted(
    (a, b) => Number(a.dir === 'inbound') - Number(b.dir === 'inbound') || b.ids.length - a.ids.length,
  );
  for (const s of seqs) {
    let run: LatLngTuple[] = [];
    let a: StationId | undefined;
    for (const b of s.ids) {
      if (a !== undefined) {
        const k = a < b ? a + '|' + b : b + '|' + a;
        if (drawn.has(k)) {
          if (run.length > 1) paths.push(run);
          run = [];
        } else {
          drawn.add(k);
          const seg = segPoints(net, lineId, a, b);
          const first = seg[0];
          if (run.length === 0 && first) run.push(first);
          const start = run.length - 1;
          for (const [j, p] of seg.entries()) if (j > 0) run.push(p);
          segments.set(a + '|' + b, { path: paths.length, start, end: run.length - 1 });
          segments.set(b + '|' + a, { path: paths.length, start: run.length - 1, end: start });
        }
      }
      a = b;
    }
    if (run.length > 1) paths.push(run);
  }
  return withSegments ? { paths, segments } : paths;
}

function findPrev(
  net: Network,
  L: NetworkLine,
  N: StationId,
  N2: StationId | undefined,
  dir: string,
  loc: string,
): StationId | null {
  const nb = L.adj.get(N);
  if (!nb || nb.size === 0) return null;
  let cands = [...nb].filter((x) => x !== N2);
  // N is a terminus the train is starting from.
  if (cands.length === 0) return null;

  // Narrow by direction of travel when TfL tells us.
  const dirSet = dir === 'outbound' ? L.outPrev.get(N) : dir === 'inbound' ? L.outNext.get(N) : null;
  if (dirSet) {
    const narrowed = cands.filter((x) => dirSet.has(x));
    if (narrowed.length > 0) cands = narrowed;
  }
  const [only] = cands;
  if (cands.length === 1 && only !== undefined) return only;

  // TfL's currentLocation often reads "Between X and Y" or "Left X".
  const l = normName(loc);
  if (l) {
    const named = cands.filter((x) => {
      const n = normName(stationOf(net, x).name);
      return n !== '' && (' ' + l + ' ').includes(' ' + n + ' ');
    });
    const [one] = named;
    if (named.length === 1 && one !== undefined) return one;
  }

  // Otherwise: the neighbor farthest from where the train goes next.
  const target = N2 === undefined ? undefined : net.stations.get(N2);
  if (target) {
    let best: StationId | null = null;
    let bd = -1;
    for (const x of cands) {
      const d = dist(stationOf(net, x), target);
      if (d > bd) {
        bd = d;
        best = x;
      }
    }
    return best;
  }
  return dir ? (cands[0] ?? null) : null;
}

// Rough time to run between two adjacent stations, including a little
// acceleration and braking. A heuristic, not TfL data.
// Fitted to live TfL gaps between consecutive predictions (Sept 2026):
// about 57 s + d/14.9 on the Tube and 56 s + d/11.8 on the Overground.
function segTime(net: Network, a: StationId, b: StationId, lineId?: LineId): number {
  const d =
    lineId !== undefined && lineId !== '' ? segLength(net, lineId, a, b) : dist(stationOf(net, a), stationOf(net, b));
  return 55 + d / 14;
}

// TfL drops trains from a response and lists them again a poll or two later,
// and gives a train a new destination (so a new key) when it reverses. So a
// train missing from a response keeps running on its own last predictions,
// and is only retired once it has been missing for a minute or is well past
// its last predicted stop (depot, siding, end of service).
const MISSING_GRACE = 60000;
const PAST_LAST_STOP = 45000;

// A tracked train: { id, key, pred, dead, missingSince, alpha, motion, render }.
// `id` never changes, so selection survives a new key. `pred` is the latest
// prediction from computeTrains and is replaced whole on every poll.
// `motion` belongs to advanceTrains and `render` to the renderer.
let nextTrackingId = 0;

// A vehicle id that identifies a train: not empty, not TfL's all-zero dummy.
function isRealVehicleId(v: string): boolean {
  return v !== '' && !/^0+$/u.test(v);
}

// Merge one arrivals response (trains from computeTrains, covering `lines`)
// into the live map of tracked trains (id -> train), in place. `retired`:
// keys taken off on purpose (ComputedTrains.retired), which fade out now.
function mergeTrains(
  trains: TrackedTrains,
  computed: readonly TrainPrediction[],
  lines: readonly LineId[],
  now: number,
  retired: ReadonlySet<string> = new Set(),
): void {
  finish(prepareMergeTrainsSteps(trains, computed, lines, now, retired))();
}

// Prepare matching and retirement without changing a train a frame can see.
// Publication keeps the live objects: selection, motion, trails and projection
// caches continue to refer to the same trains, including after a key change.
function* prepareMergeTrainsSteps(
  trains: TrackedTrains,
  computed: readonly TrainPrediction[],
  lines: readonly LineId[],
  now: number,
  retired: ReadonlySet<string> = new Set(),
): Generator<void, () => void, void> {
  const keys = new Set<string>();
  for (const c of computed) {
    keys.add(c.key);
    yield;
  }
  const coveredLines = new Set(lines);
  const byKey = new Map<string, TrackedTrain>();
  const byVehicle = new Map<LineId, Map<string, TrackedTrain[]>>();
  const staged: TrackedTrain[] = [];
  const changed = new Set<TrackedTrain>();
  for (const live of trains.values()) {
    const t = { ...live };
    staged.push(t);
    byKey.set(t.key, t);
    if (!keys.has(t.key)) {
      let vehicles = byVehicle.get(t.pred.lineId);
      if (!vehicles) byVehicle.set(t.pred.lineId, (vehicles = new Map<string, TrackedTrain[]>()));
      let matches = vehicles.get(t.pred.vid);
      if (!matches) vehicles.set(t.pred.vid, (matches = []));
      matches.push(t);
    }
    yield;
  }
  for (const c of computed) {
    let t = byKey.get(c.key);
    if (!t && isRealVehicleId(c.vid)) {
      // The same vehicle under a new key (a new trip or destination) is the
      // same drawn train, so it carries on instead of crossfading. A vehicle
      // whose trips are all still listed keeps one tracked train per trip.
      t = byVehicle
        .get(c.lineId)
        ?.get(c.vid)
        ?.find((o) => !keys.has(o.key));
    }
    if (!t) {
      t = {
        id: '',
        key: c.key,
        pred: c,
        dead: false,
        missingSince: null,
        alpha: 0,
        motion: {},
        render: {},
      };
      staged.push(t);
    }
    byKey.delete(t.key);
    byKey.set(c.key, t);
    t.key = c.key;
    t.pred = c;
    t.dead = false;
    t.missingSince = null;
    changed.add(t);
    yield;
  }
  for (const t of staged) {
    if (coveredLines.has(t.pred.lineId) && !keys.has(t.key) && !t.dead) {
      t.missingSince ??= now;
      const last = t.pred.stops?.at(-1)?.eta ?? t.pred.eta;
      if (retired.has(t.key) || now - t.missingSince > MISSING_GRACE || now - last > PAST_LAST_STOP) t.dead = true;
      changed.add(t);
    }
    yield;
  }
  return () => {
    for (const t of changed) {
      const live = trains.get(t.id);
      if (live) {
        live.key = t.key;
        live.pred = t.pred;
        live.dead = t.dead;
        live.missingSince = t.missingSince;
      } else if (keys.has(t.key)) {
        // A fading train may have been removed by a frame while preparing.
        const added = { ...t, id: 't' + ++nextTrackingId, alpha: 0, motion: {}, render: {} };
        trains.set(added.id, added);
      }
    }
  };
}

// Frames between motion steps of a train away from the view (advanceTrains).
const AWAY_STEPS = 8;
// Per train away from the view: which of the AWAY_STEPS frames it steps on,
// the prediction of its last step and the ms since.
const away = new WeakMap<TrackedTrain, { readonly slot: number; pred: TrainPrediction; ms: number }>();
let awaySlots = 0;
const awayFrames = new WeakMap<TrackedTrains, number>();

// Eligibility and drawing use the same world projection. Offscreen motion
// steps are staggered, and stopped trains may get a new position object with
// unchanged coordinates, so keep it until the coordinates or projector change.
interface ProjectedTrainPosition extends ScreenPoint {
  readonly metersToWorld: number;
}
const trainProjections = new WeakMap<
  TrackedTrain,
  {
    readonly lat: number;
    readonly lon: number;
    readonly project: (lat: number, lon: number) => ScreenPoint;
    readonly point: ProjectedTrainPosition;
  }
>();

function projectTrain(
  t: TrackedTrain,
  pos: LatLon,
  project: (lat: number, lon: number) => ScreenPoint,
): ProjectedTrainPosition {
  const cached = trainProjections.get(t);
  if (cached && cached.lat === pos.lat && cached.lon === pos.lon && cached.project === project) return cached.point;
  const world = project(pos.lat, pos.lon);
  // A 256 px Mercator world at zoom 0, as in view.ts and the renderer.
  const point = { ...world, metersToWorld: 256 / (40_075_017 * Math.cos(pos.lat * RAD)) };
  trainProjections.set(t, { lat: pos.lat, lon: pos.lon, project, point });
  return point;
}

// One animation step for every tracked train: motion toward its prediction,
// then fading in or out. Returns the ids of trains that finished fading out
// and were removed.
//
// near(t, m): whether the train may be drawn this frame, its motion.pos m
// meters or less from where a step would put it. Trains it rules out step
// every AWAY_STEPS frames with the time they missed (spread over the
// frames, so no frame steps them all), and at once on a new prediction, as
// a poll's jump into view would. Their fading goes on every frame.
function advanceTrains(
  net: Network,
  trains: TrackedTrains,
  now: number,
  dt: number,
  reset = false,
  near?: (t: TrackedTrain, meters: number) => boolean,
): TrainId[] {
  const removed: TrainId[] = [];
  const frame = awayFrames.get(trains) ?? 0;
  awayFrames.set(trains, (frame + 1) % AWAY_STEPS);
  for (const [id, t] of trains) {
    const prev = t.motion.pos;
    let step = dt;
    let skip = false;
    if (near && prev !== undefined) {
      let a = away.get(t);
      if (!a) away.set(t, (a = { slot: awaySlots++ % AWAY_STEPS, pred: t.pred, ms: 0 }));
      step = a.ms + dt;
      skip =
        !reset &&
        a.pred === t.pred &&
        a.slot !== frame &&
        !near(t, (MAX_SPEED * Math.sqrt(t.pred.reach ?? 1) * step) / 1000);
      a.pred = t.pred;
      a.ms = skip ? step : 0;
    }
    if (!skip) {
      const pos = trainDisplay(net, t.pred, t.motion, now, step, reset);
      t.motion.moving =
        prev !== undefined &&
        pos.P !== null &&
        pos.P !== '' &&
        (pos.segment !== prev.segment || Math.abs(pos.f - prev.f) > 1e-7);
      t.motion.pos = pos;
    }
    t.alpha += ((t.dead ? 0 : 1) - t.alpha) * Math.min(1, dt / 500);
    if (t.dead && t.alpha < 0.02) {
      trains.delete(id);
      removed.push(id);
    }
  }
  return removed;
}

// ---- stops
//
// Trains stand at every station they call at. TfL only publishes arrival
// times, and the gap between two arrivals is the dwell plus the run, so
// without a departure time a train leaves DWELL after it arrives, or a third
// of the gap to its next arrival if that's less.

// ms, a Tube or Overground dwell
const DWELL = 20000;
// ms a train stands even when it gets there late
const MIN_DWELL = 8000;

// When a train leaves `stop`, given its next one. A departure equal to the
// arrival is a known one too: a train standing at its next station has its
// departure as its eta (callsAhead); feeds only give later ones.
function departure(stop: Stop, next: Stop | undefined): number {
  if (stop.pass === true) return stop.eta;
  if (stop.dep !== undefined && stop.dep >= stop.eta) return stop.dep;
  const gap = next ? next.eta - stop.eta : Infinity;
  return stop.eta + Math.max(0, Math.min(DWELL, gap / 3));
}

// The running part of a gap between two arrivals (s), without the dwell.
function runPart(gapS: number): number {
  return gapS - Math.min(DWELL / 1000, gapS / 3);
}

// The prediction's calls, its next station first. A train with no previous
// station (P) is standing at N, and its eta there is when it leaves.
function callsAhead(t: TrainPrediction): Stop[] {
  const stops = t.stops ?? [];
  const first = stops[0];
  const rest = first?.sid === t.N ? stops.slice(1) : stops;
  const standing = t.P === null || t.P === '';
  const head: Stop = {
    ...(first?.sid === t.N ? first : {}),
    sid: t.N,
    eta: t.eta,
    ...(standing ? { dep: t.eta } : {}),
  };
  return [head, ...rest];
}

type TrainProgress = Pick<TrainTarget, 'P' | 'N' | 'f' | 'eta'>;

// The motion calculation needs progress along the route, not another
// geographic projection of the estimate on every animation frame.
function trainProgress(net: Network, t: TrainPrediction, now: number): TrainProgress {
  // Follow already published arrivals between polls, standing at each
  // station until it's due out. Holding at the first stop until a refresh
  // makes trains pause, then jump into the next segment.
  if (!t.waiting && t.stops !== undefined && now > t.eta) {
    const calls = callsAhead(t);
    let previous: Stop = calls[0] ?? { sid: t.N, eta: t.eta };
    let projected: TrainProgress | null = null;
    for (const next of calls.slice(1)) {
      if (next.eta <= previous.eta || net.lines[t.lineId]?.adj.get(previous.sid)?.has(next.sid) !== true) break;
      const leaves = Math.min(departure(previous, next), next.eta);
      const span = next.eta - leaves;
      const f = span > 0 ? Math.max(0, Math.min(1, (now - leaves) / span)) : now >= next.eta ? 1 : 0;
      projected = {
        f,
        P: previous.sid,
        N: next.sid,
        eta: next.eta,
      };
      if (now <= next.eta) return projected;
      previous = next;
    }
    if (projected) return projected;
  }
  if (t.P === null || t.P === '') {
    return { f: 1, P: t.P, N: t.N, eta: t.eta };
  }
  const tts = (t.eta - now) / 1000;
  let f = t.segT > 0 ? 1 - tts / t.segT : 1;
  if (/^at platform$/iu.test(t.loc) && tts < 40) f = 1;
  f = Math.max(0, Math.min(1, f));
  return { f, P: t.P, N: t.N, eta: t.eta };
}

// Where should this train be drawn right now?
// Follows the track shape when there is one; ang is the track's heading.
function trainTarget(net: Network, t: TrainPrediction, now: number): TrainTarget {
  const target = trainProgress(net, t, now);
  if (target.P === null || target.P === '') {
    const N = stationOf(net, target.N);
    const next = t.stops[1]?.sid;
    const ang = next !== undefined && net.stations.has(next) ? along(net, t.lineId, target.N, next, 0).ang : null;
    return { ...target, lat: N.lat, lon: N.lon, ang };
  }
  return { ...target, ...along(net, t.lineId, target.P, target.N, target.f) };
}

// ---- displayed motion
//
// trainDisplay(net, pred, motion, ...) reads the prediction and keeps its
// own state (rail, sd, vd, railStops) in `motion`.
//
// Each train keeps a rail: the stations it has passed and will pass, with
// cumulative track distance. The displayed train only ever moves forward
// along it, and never past its next call: it brakes into it to arrive at
// the time TfL predicts (stations are the only points TfL predicts at all),
// stands there until it's due out, then goes on. Speed changes are
// acceleration limited, so a new poll changes how fast a train goes, never
// where it is. The one exception is a poll that puts the
// train on a route that doesn't connect with the one drawn (or far from
// it): then it jumps, with `snapped` saying why, so the renderer can crossfade.

// m/s², only smooths speed changes between polls
const ACCEL = 1.5;
// m/s, catch-up cap (Elizabeth line tops out near 40)
const MAX_SPEED = 35;
// m the estimate may lead before we give up and jump
const SNAP_AHEAD = 1500;
// m the drawn train may lead before we jump back
const SNAP_BEHIND = 2500;
// m/s², braking into a station
const BRAKE = 1;
// m/s, the least a train overdue at its next station runs at
const CATCH_UP = 8;

function railOf(net: Network, lineId: LineId, ids: readonly StationId[]): Rail {
  const cum = [0];
  let total = 0;
  let prev: StationId | undefined;
  for (const id of ids) {
    if (prev !== undefined) {
      total += segLength(net, lineId, prev, id);
      cum.push(total);
    }
    prev = id;
  }
  return { ids, cum };
}

// Stations from the estimate's segment onward, as far as the published
// stops stay connected. Matched by visit so a loop reset keeps the later
// call and not the earlier one with the same station.
function targetChain(net: Network, t: TrainPrediction, target: TrainProgress): StationId[] {
  const adj = net.lines[t.lineId]?.adj;
  const ids = target.P !== null && target.P !== '' ? [target.P, target.N] : [target.N];
  const stops = t.stops ?? [];
  let j = stops.findIndex((s) => s.sid === target.N && s.eta === target.eta);
  if (j < 0) j = stops.findIndex((s) => s.sid === target.N);
  if (j >= 0) {
    let last = target.N;
    for (j++; j < stops.length; j++) {
      const stop = stops[j];
      if (!stop || adj?.get(last)?.has(stop.sid) !== true) break;
      ids.push(stop.sid);
      last = stop.sid;
    }
  }
  return ids;
}

// Distance along the rail for a { P, N, f } position, or null if it isn't on
// it. A loop's rail passes the same place more than once: the visit nearest
// near (the first without it).
function railDistance(rail: Rail, P: StationId | null, N: StationId, f: number, near?: number): number | null {
  const { ids, cum } = rail;
  let best: number | null = null;
  for (let i = 0; i < ids.length; i++) {
    if (ids[i] !== N) continue;
    const here = cum[i] ?? 0;
    let s: number | null = null;
    if (P === null || P === '') s = here;
    else if (i > 0 && ids[i - 1] === P) {
      const before = cum[i - 1] ?? 0;
      s = before + (here - before) * f;
    }
    if (s === null) continue;
    if (near === undefined) return s;
    if (best === null || Math.abs(s - near) < Math.abs(best - near)) best = s;
  }
  return best;
}

function railPosition(net: Network, lineId: LineId, rail: Rail, s: number): RailPoint {
  const { ids, cum } = rail;
  const first = ids[0];
  if (first === undefined) throw new Error('empty rail');
  if (ids.length < 2 || s <= 0) {
    const next = ids[1];
    const N = stationOf(net, first);
    const ang = next !== undefined && next !== '' ? along(net, lineId, first, next, 0, rail).ang : null;
    return { lat: N.lat, lon: N.lon, ang, P: null, N: first, f: 1 };
  }
  let i = 1;
  while (i < ids.length - 1 && (cum[i] ?? 0) < s) i++;
  const P = ids[i - 1];
  const N = ids[i];
  const cP = cum[i - 1];
  const cN = cum[i];
  if (P === undefined || N === undefined || cP === undefined || cN === undefined) throw new Error('broken rail');
  const len = cN - cP;
  const f = len > 0 ? Math.max(0, Math.min(1, (s - cP) / len)) : 1;
  return { ...along(net, lineId, P, N, f, rail), P, N, f };
}

// Which call (an index into calls) each point of the rail is, by visit: a
// loop's second call at a station keeps its own times. The calls run along
// the rail one point each (targetChain), so they're lined up at the
// estimate: the call it's heading for at its station on the rail at or
// past sT, the estimate's distance along it.
function railCalls(
  rail: Rail,
  calls: readonly Stop[],
  target: TrainProgress,
  sT: number,
): readonly (number | undefined)[] {
  const { ids, cum } = rail;
  const at = Array.from<number | undefined>({ length: ids.length });
  let k = calls.findIndex((c) => c.sid === target.N && c.eta === target.eta);
  if (k < 0) k = calls.findIndex((c) => c.sid === target.N);
  let j = ids.findIndex((id, i) => id === target.N && (cum[i] ?? 0) >= sT - 0.5);
  if (j < 0) j = ids.indexOf(target.N);
  if (k < 0 || j < 0) {
    k = 0;
    j = ids.indexOf(calls[0]?.sid ?? '');
    if (j < 0) return at;
  }
  for (let d = 0; j + d < ids.length && ids[j + d] === calls[k + d]?.sid; d++) at[j + d] = k + d;
  for (let d = 1; j - d >= 0 && k - d >= 0 && ids[j - d] === calls[k - d]?.sid; d++) at[j - d] = k - d;
  return at;
}

// The first point of the rail at station id at or ahead of s, or -1.
function railAhead(rail: Rail, id: StationId, s: number): number {
  return rail.ids.findIndex((x, i) => x === id && (rail.cum[i] ?? 0) >= s - 0.5);
}

// m a drawn train may be from a new rail and carry on along it
const ON_RAIL = 150;
// m of that which it moves without a crossfade
const SIDESTEP = 25;

// Distance along the rail nearest to p, if the rail passes within ON_RAIL
// and runs the way p is heading there (so it never turns a train round).
function nearOnRail(
  net: Network,
  lineId: LineId,
  rail: Rail,
  p: LatLon & { readonly ang: number | null },
): { readonly s: number; readonly d: number } | null {
  let best: { s: number; d: number } | null = null;
  for (let i = 1; i < rail.ids.length; i++) {
    const a = rail.ids[i - 1];
    const b = rail.ids[i];
    const c0 = rail.cum[i - 1];
    const c1 = rail.cum[i];
    if (a === undefined || b === undefined || c0 === undefined || c1 === undefined) continue;
    const { f, d } = projectOnto(net, lineId, a, b, p);
    if (d > ON_RAIL || (best && d >= best.d)) continue;
    const ang = along(net, lineId, a, b, f).ang;
    if (p.ang !== null && Math.cos(ang - p.ang) <= 0) continue;
    best = { s: c0 + (c1 - c0) * f, d };
  }
  return best;
}

// Predictions are replaced whole on a poll. Between polls the call-to-rail
// mapping changes only at a new target station/visit, including later laps
// of a loop. Keep it per train instead of rebuilding it every frame.
const callSchedules = new WeakMap<
  TrainMotion,
  {
    readonly pred: TrainPrediction;
    readonly rail: Rail;
    readonly target: StationId;
    readonly eta: number;
    readonly visit: number;
    readonly listed: readonly (number | undefined)[];
    readonly timing: ({ readonly eta: number; readonly dep: number } | undefined)[];
  }
>();

function trainDisplay(
  net: Network,
  pred: TrainPrediction,
  motion: TrainMotion,
  now: number,
  dt: number,
  reset = false,
): DisplayPosition {
  const target = trainProgress(net, pred, now);
  const out = (rail: Rail, s: number, snapped: SnapReason | false): DisplayPosition => {
    motion.sd = s;
    const p = railPosition(net, pred.lineId, rail, s);
    return { ...p, segment: pred.lineId + '|' + String(p.P) + '|' + p.N, snapped };
  };
  const place = (snapped: SnapReason | false): DisplayPosition => {
    const chain = targetChain(net, pred, target);
    const rail = railOf(net, pred.lineId, chain);
    motion.rail = rail;
    motion.railStops = pred.stops;
    motion.vd = 0;
    motion.dwell = undefined;
    motion.owed = undefined;
    // `|| 0`: not on the rail (or NaN) starts at its beginning.
    const found = railDistance(rail, target.P, target.N, target.f);
    const s = found === null || Number.isNaN(found) ? 0 : found;
    const d = out(rail, s, snapped);
    // Report the estimate exactly; the rail distance is only for motion.
    return target.P !== null && target.P !== ''
      ? { ...d, ...trainTarget(net, pred, now), segment: d.segment, snapped }
      : d;
  };
  let rail = motion.rail;
  // A visible sideways move onto a new rail, for the renderer to crossfade.
  let moved: SnapReason | false = false;
  let sd = motion.sd;
  if (reset || !rail || sd === undefined) return place(false);

  // A poll brought new stops: rebuild the rail from the estimate onward,
  // keeping the drawn train's own position on it.
  let targetDistance = railDistance(rail, target.P, target.N, target.f, sd);
  if (motion.railStops !== pred.stops || targetDistance === null) {
    const chain = targetChain(net, pred, target);
    const here = railPosition(net, pred.lineId, rail, sd);
    const prior = rail;
    let next = railOf(net, pred.lineId, chain);
    let s = railDistance(next, here.P, here.N, here.f);
    if (s === null) {
      const q = prior.ids.indexOf(chain[0] ?? target.N);
      const behind = here.P !== null && here.P !== '' ? prior.ids.indexOf(here.P) : prior.ids.indexOf(here.N);
      if (q >= 0 && behind >= 0 && q >= behind) {
        // The estimate is ahead of the drawn train: keep the stretch between.
        next = railOf(net, pred.lineId, prior.ids.slice(0, q).concat(chain));
        s = railDistance(next, here.P, here.N, here.f);
      } else if (q >= 0 && behind >= 0 && chain.every((id, i) => prior.ids[q + i] === id)) {
        // A late poll puts the estimate behind the drawn train on the same
        // route, with fewer stops ahead: keep the old rail past its end, so
        // the train slows down where it is instead of snapping back.
        next = railOf(net, pred.lineId, prior.ids.slice(0, q).concat(chain, prior.ids.slice(q + chain.length)));
        s = railDistance(next, here.P, here.N, here.f);
      }
    }
    if (s === null) {
      // The same track under other stations: TfL lists a Metropolitan train
      // through Willesden Green (where it doesn't stop) one poll and not the
      // next, so Wembley Park-Finchley Road becomes Wembley Park-Willesden
      // Green-Finchley Road. Carry on from the nearest point on the new rail.
      next = railOf(net, pred.lineId, chain);
      const near = nearOnRail(net, pred.lineId, next, here);
      s = near ? near.s : null;
      if (near && near.d > SIDESTEP) moved = 'route';
    }
    if (s === null) return place('route');
    // Keep a little history so trails have somewhere to go.
    let cut = 0;
    while (cut < next.ids.length - 2 && (next.cum[cut + 1] ?? 0) < s - 3000) cut++;
    if (cut) {
      // Same place, less the history dropped: s may be from nearOnRail, off
      // the old station pair.
      s -= next.cum[cut] ?? 0;
      next = railOf(net, pred.lineId, next.ids.slice(cut));
    }
    // TfL's ETAs often run late, so a poll can drop a call the drawn train
    // is still heading for: it stops there all the same. Owed calls are the
    // next visit (ahead of the drawn train, or where it stands) to their
    // station that no new call is.
    const listed = railCalls(next, callsAhead(pred), target, railDistance(next, target.P, target.N, target.f) ?? 0);
    const owed = new Set<StationId>();
    const dropped = (id: StationId): boolean => {
      const j = railAhead(next, id, s);
      return j >= 0 && listed[j] === undefined;
    };
    for (const id of motion.owed ?? []) if (dropped(id)) owed.add(id);
    for (const c of motion.railStops ?? []) if (c.pass !== true && dropped(c.sid)) owed.add(c.sid);
    motion.owed = owed.size > 0 ? owed : undefined;
    rail = next;
    sd = s;
    motion.rail = rail;
    motion.railStops = pred.stops;
    motion.sd = sd;
    targetDistance = railDistance(rail, target.P, target.N, target.f, sd);
  }

  const { ids, cum } = rail;
  // Off the rail only when the estimate fell behind the history kept above.
  // The math below always treated that null as 0. On a loop, the visit
  // nearest the drawn train: the rail is only rebuilt when a poll brings new
  // stops, so between polls the estimate goes round onto later laps of it.
  const sT = targetDistance ?? 0;
  const reach = pred.reach ?? 1;
  // The estimate's place between stations is only a guess from a run time
  // (TfL's for Wembley Park-Finchley Road swung from 550 s to 340 s as its
  // Metropolitan listings changed, moving the estimate 3.5 km). Its next
  // station and ETA are what it knows. So a drawn train short of that
  // station, able to get there in time, just paces itself to it; it jumps
  // only past it (back) or unable to make it (forward).
  const jN = railAhead(rail, target.N, sT);
  const sN = jN < 0 ? null : (cum[jN] ?? null);
  const toN = sN === null ? null : sN - sd;
  const makes =
    toN !== null && toN >= 0 && toN / Math.max(1, (target.eta - now) / 1000) <= MAX_SPEED * Math.sqrt(reach);
  if (sT - sd > SNAP_AHEAD * reach && !makes) return place('ahead');
  if (sd - sT > SNAP_BEHIND * Math.sqrt(reach) && !(toN !== null && toN >= 0)) return place('behind');

  // The calls ahead: when the train gets to each, and when it leaves.
  // By point of the rail, so each visit to a station has its own.
  let schedule = callSchedules.get(motion);
  if (
    !schedule ||
    schedule.pred !== pred ||
    schedule.rail !== rail ||
    schedule.target !== target.N ||
    schedule.eta !== target.eta ||
    schedule.visit !== jN
  ) {
    const calls = callsAhead(pred);
    const listed = railCalls(rail, calls, target, sT);
    schedule = {
      pred,
      rail,
      target: target.N,
      eta: target.eta,
      visit: jN,
      listed,
      timing: listed.map((k) => {
        const c = k === undefined ? undefined : calls[k];
        return c && c.pass !== true && k !== undefined ? { eta: c.eta, dep: departure(c, calls[k + 1]) } : undefined;
      }),
    };
    callSchedules.set(motion, schedule);
  }
  const { listed } = schedule;
  // Overdue calls depend on this frame's time; never write them into the cache.
  const timing = motion.owed ? schedule.timing.slice() : schedule.timing;
  // Owed calls are overdue: get there, stand a moment, go.
  for (const id of motion.owed ?? []) {
    const j = railAhead(rail, id, sd);
    if (j >= 0 && listed[j] === undefined) timing[j] = { eta: now, dep: 0 };
  }

  // Standing at a call: stay until it's due out, and a moment even if late.
  let standing = false;
  for (let j = 0; j < ids.length; j++) {
    const id = ids[j];
    const call = timing[j];
    if (id === undefined || !call || Math.abs((cum[j] ?? 0) - sd) > 0.5) continue;
    standing = true;
    if (motion.dwell?.at !== id) motion.dwell = { at: id, since: now };
    if (now < Math.max(call.dep, motion.dwell.since + MIN_DWELL)) {
      motion.vd = 0;
      return out(rail, Math.max(sd, cum[j] ?? sd), moved);
    }
    // Due out: an owed call is made, and a later visit isn't owed.
    if (motion.owed?.has(id) === true && listed[j] === undefined) {
      const owed = new Set(motion.owed);
      owed.delete(id);
      motion.owed = owed.size > 0 ? owed : undefined;
    }
  }
  // Gone: the next visit to the same station is a new stand.
  if (!standing) motion.dwell = undefined;

  // The next call ahead: the train goes no further than it, braking in to
  // arrive at its ETA.
  let want = 0;
  const railEnd = cum.at(-1) ?? 0;
  let stopAt = railEnd;
  for (let j = 0; j < ids.length; j++) {
    const at = cum[j] ?? 0;
    const call = timing[j];
    if (at <= sd + 0.5 || !call) continue;
    stopAt = at;
    const d = at - sd;
    const T = (call.eta - now) / 1000;
    // The cruising speed that covers d in T, braking at BRAKE at the end:
    // d = v T - v² / 2 BRAKE. Overdue (or not possible): catch up.
    const disc = (BRAKE * T) ** 2 - 2 * BRAKE * d;
    want = T >= 1 && disc >= 0 ? BRAKE * T - Math.sqrt(disc) : Math.max(CATCH_UP, (sT - sd) / 3);
    break;
  }
  // Waiting at the origin, or nothing published beyond this point: hold at
  // the estimate, easing into it rather than overrunning.
  // Also at a station the estimate holds at until its ETA (a train at the
  // platform), even with stops published beyond it.
  const holding = target.P === null || target.P === '' || (target.f >= 1 && (sT >= railEnd - 0.01 || target.eta > now));
  if (holding) {
    stopAt = Math.min(stopAt, sT);
    want = Math.min(want || Infinity, Math.max(0, (sT - sd) / 3));
    if (!Number.isFinite(want)) want = 0;
  } else if (!want && sT > sd) {
    // No call ahead on the rail: follow the estimate.
    want = (sT - sd) / 3;
  }
  // Brake to stop where it has to, and creep the last meters.
  want = Math.min(MAX_SPEED * Math.sqrt(reach), want, Math.max(0.5, Math.sqrt(2 * BRAKE * Math.max(0, stopAt - sd))));
  const step = (ACCEL * Math.max(0, dt)) / 1000;
  // vd is never NaN once set (the check below zeroes it), so ?? acts as ||.
  const vd0 = motion.vd ?? 0;
  let vd = vd0 + Math.max(-step, Math.min(step, want - vd0));
  if (!(vd > 0)) vd = 0;
  motion.vd = vd;
  const s = Math.min(stopAt, sd + (vd * Math.max(0, dt)) / 1000);
  return out(rail, Math.max(sd, s), moved);
}

// The rails and train centers use the same mitered screen-space path.
function normal(a: ScreenPoint, b: ScreenPoint): ScreenPoint {
  const dx = b.x - a.x,
    dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  return { x: dy / len, y: -dx / len };
}

function offsetPoints(points: readonly ScreenPoint[], gap: number): ScreenPoint[] {
  return points.map((p, i) => {
    const prev = i > 0 ? points[i - 1] : undefined;
    const next = points[i + 1];
    const before = prev ? normal(prev, p) : null;
    const after = next ? normal(p, next) : null;
    const a = before ?? after;
    if (!a) return { ...p };
    const b = after ?? a;
    let nx = a.x + b.x,
      ny = a.y + b.y;
    const len = Math.hypot(nx, ny);
    if (len < 1e-6) {
      nx = a.x;
      ny = a.y;
    } else {
      nx /= len;
      ny /= len;
    }
    const miter = gap / Math.max(0.5, nx * a.x + ny * a.y);
    return { x: p.x + nx * miter, y: p.y + ny * miter };
  });
}

// A track projected once, drawable at any zoom and on either rail (see
// TrackView). offsetPoints is linear in the gap and its directions don't
// change with scale, so the rail at scale k and gap g is exactly
// k * point + g * miter, with every distance along it k times longer.
function projectedTrack(points: readonly ScreenPoint[]): ProjectedTrack {
  const cum = [0];
  let len = 0;
  let prev: ScreenPoint | undefined;
  for (const p of points) {
    if (prev) {
      len += Math.hypot(p.x - prev.x, p.y - prev.y);
      cum.push(len);
    }
    prev = p;
  }
  const miters = offsetPoints(points, 1).map((q, i) => {
    const p = points[i];
    return p ? { x: q.x - p.x, y: q.y - p.y } : { x: 0, y: 0 };
  });
  return { points, miters, cum, len };
}

// Drawn as projected, on the centerline.
const FLAT: TrackView = { k: 1, gap: 0 };

// Point i of a track as drawn.
function vertex(track: ProjectedTrack, i: number, view: TrackView): ScreenPoint | undefined {
  const p = track.points[i];
  const m = track.miters[i];
  if (!p || !m) return undefined;
  return { x: p.x * view.k + m.x * view.gap, y: p.y * view.k + m.y * view.gap };
}

// Every point of a track as drawn: offsetPoints of the scaled centerline.
function placeTrack(track: ProjectedTrack, view: TrackView = FLAT): ScreenPoint[] {
  return track.points.map((_, i) => vertex(track, i, view) ?? { x: 0, y: 0 });
}

// The drawn point at distance s (in track units, already within the track).
function trackAt(track: ProjectedTrack, s: number, view: TrackView): ScreenPoint {
  const { cum } = track;
  let lo = 0;
  let hi = cum.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((cum[mid] ?? 0) <= s) lo = mid;
    else hi = mid;
  }
  const a = vertex(track, lo, view),
    b = vertex(track, hi, view);
  const clo = cum[lo];
  const chi = cum[hi];
  if (!a || !b || clo === undefined || chi === undefined) throw new Error('empty projected track');
  const u = (s - clo) / (chi - clo || 1);
  return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
}

// The drawn point a fraction f along a track, and the heading there.
// headingStart and headingEnd are distances in track units.
function trackPoint(
  track: ProjectedTrack,
  f: number,
  headingStart = 0,
  headingEnd = track.len,
  view: TrackView = FLAT,
): ScreenTrackPoint {
  const sample = (distance: number): ScreenPoint => trackAt(track, Math.max(0, Math.min(track.len, distance)), view);
  const distance = Math.max(0, Math.min(1, f)) * track.len;
  // Orient the pill over its visible length (6 drawn px either side), rather
  // than every tiny vertex. At a station, use this segment's tangent.
  // Averaging across a sharp station join points a stopped train across the
  // rails instead of along them.
  const reach = 6 / view.k;
  const a = sample(Math.max(headingStart, distance - reach));
  const b = sample(Math.min(headingEnd, distance + reach));
  return { ...sample(distance), ang: Math.atan2(b.y - a.y, b.x - a.x) };
}

// Drawn points along the track from distance `from` to distance `to` (track
// units, either order), including every vertex in between. Used for trails.
function trackSlice(track: ProjectedTrack, from: number, to: number, view: TrackView = FLAT): ScreenPoint[] {
  const clampD = (d: number): number => Math.max(0, Math.min(track.len, d));
  const start = clampD(from);
  const end = clampD(to);
  const { cum } = track;
  const out = [trackAt(track, start, view)];
  if (start < end) {
    for (let i = 0; i < cum.length; i++) {
      const c = cum[i];
      const p = c !== undefined && c > start && c < end ? vertex(track, i, view) : undefined;
      if (p) out.push(p);
    }
  } else {
    for (let i = cum.length - 1; i >= 0; i--) {
      const c = cum[i];
      const p = c !== undefined && c < start && c > end ? vertex(track, i, view) : undefined;
      if (p) out.push(p);
    }
  }
  out.push(trackAt(track, end, view));
  return out;
}

// Which way a train between two adjacent stations is going: 'out' (TfL's
// outbound), 'in', or null when they aren't next to each other.
function wayOf(line: NetworkLine, a: StationId, b: StationId): 'out' | 'in' | null {
  if (line.outNext.get(a)?.has(b) === true || line.outPrev.get(b)?.has(a) === true) return 'out';
  if (line.outPrev.get(a)?.has(b) === true || line.outNext.get(b)?.has(a) === true) return 'in';
  return line.adj.get(a)?.has(b) === true ? 'in' : null;
}

// A line's stations on from `from` along `on` (outNext or outPrev: a train
// can't turn back at a junction), not through `seen`; where TfL's
// direction data has nothing for a station, its neighbors either way.
// While there's a single way on (a junction or the end of the line stops
// it), or, given a station to reach (`to`), the shortest way there.
function walkLine(
  line: NetworkLine,
  on: ReadonlyMap<StationId, ReadonlySet<StationId>>,
  from: StationId,
  seen: ReadonlySet<StationId>,
  to: StationId | null,
): StationId[] {
  const next = (at: StationId, not: ReadonlySet<StationId>): StationId[] => {
    const directed = on.get(at);
    const all = directed && directed.size > 0 ? directed : (line.adj.get(at) ?? new Set<StationId>());
    return [...all].filter((x) => !not.has(x));
  };
  if (to !== null && !seen.has(to)) {
    const back = new Map<StationId, StationId>();
    const done = new Set([...seen, from]);
    for (let queue = [from]; queue.length > 0 && !back.has(to);) {
      const q: StationId[] = [];
      for (const at of queue) {
        for (const n of next(at, done)) {
          done.add(n);
          back.set(n, at);
          q.push(n);
        }
      }
      queue = q;
    }
    if (back.has(to)) {
      const path: StationId[] = [];
      for (let at: StationId | undefined = to; at !== undefined && at !== from; at = back.get(at)) path.push(at);
      return path.toReversed();
    }
  }
  const out: StationId[] = [];
  const done = new Set(seen);
  for (let at = from; ;) {
    done.add(at);
    const ways = next(at, done);
    const [only] = ways;
    if (ways.length !== 1 || only === undefined) break;
    out.push(only);
    at = only;
  }
  return out;
}

// The stations a train has already passed, from the line (TfL predicts
// only the ones ahead, `ahead` in order): back from the next station as far
// as there's one way it can have come.
function stationsBehind(line: NetworkLine | undefined, ahead: readonly StationId[]): StationId[] {
  const [first, second] = ahead;
  if (!line || first === undefined || second === undefined) return [];
  const way = wayOf(line, first, second);
  if (way === null) return [];
  return walkLine(line, way === 'out' ? line.outPrev : line.outNext, first, new Set(ahead), null).toReversed();
}

// The stations after TfL's last prediction (it predicts a quarter of an
// hour or so ahead), to the destination station `dest` when the line has
// one of that name, else as far as there's one way on.
function stationsAfter(
  line: NetworkLine | undefined,
  ahead: readonly StationId[],
  dest: StationId | null,
): StationId[] {
  const last = ahead.at(-1);
  const before = ahead.at(-2);
  if (!line || last === undefined || before === undefined || last === dest) return [];
  const way = wayOf(line, before, last);
  if (way === null) return [];
  return walkLine(line, way === 'out' ? line.outNext : line.outPrev, last, new Set(ahead), dest);
}

export {
  stationsBehind,
  departure,
  runPart,
  projectOnto,
  stationsAfter,
  dist,
  cleanName,
  normName,
  baseName,
  either,
  isRealVehicleId,
  buildNetwork,
  buildNetworkSteps,
  linePaths,
  mergeTrains,
  prepareMergeTrainsSteps,
  advanceTrains,
  projectTrain,
  trainTarget,
  findPrev,
  segTime,
  attachGeometry,
  attachGeometrySteps,
  isGeometryFile,
  decodePolyline,
  segPoints,
  segLength,
  along,
  trainDisplay,
  offsetPoints,
  projectedTrack,
  placeTrack,
  trackPoint,
  trackSlice,
};
